
import React, { useState, useMemo } from 'react';
import './ShopPage.css';
import { useApp } from '../../context/AppContext';
import ProductCard from '../../components/ProductCard/ProductCard';

const ShopPage: React.FC = () => {
  const { products, currentUser } = useApp();
  const [sortBy, setSortBy] = useState<'hot' | 'newest' | 'lowest'>('hot');

  const liveItems = useMemo(() => {
    const list = [...products];
    switch (sortBy) {
      case 'newest':
        list.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        break;
      case 'lowest':
        list.sort((a, b) => (a.salePrice || a.price) - (b.salePrice || b.price));
        break;
      default:
        // hot: most offers first, then newest
        list.sort((a, b) => {
          const oa = (a.offers?.length || 0);
          const ob = (b.offers?.length || 0);
          if (ob !== oa) return ob - oa;
          return b.createdAt.getTime() - a.createdAt.getTime();
        });
    }
    return list;
  }, [products, sortBy]);

  const wishlistProducts = products.filter(p => currentUser.wishlist.includes(p.id));

  return (
    <div className="shop-page">
      <div className="shop-hero">
        <span className="shop-eyebrow">1-of-1 Auctions</span>
        <h1>Jalen's Closet</h1>
        <p>Real pieces from my life. Submit your offer, win the bid, take it home.</p>
        <div className="shop-hero-pills">
          <span className="shop-pill">✨ One of a kind</span>
          <span className="shop-pill">⏱ Time-limited offers</span>
          <span className="shop-pill">💸 PayPal secured</span>
        </div>
      </div>

      {wishlistProducts.length > 0 && (
        <div className="wishlist-section">
          <h2 className="section-title">❤️ Your Watchlist ({wishlistProducts.length})</h2>
          <div className="wishlist-scroll">
            {wishlistProducts.map(product => (
              <div key={product.id} className="wishlist-item">
                <ProductCard product={product} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="shop-controls">
        <div className="sort-tabs" role="tablist">
          {([
            { id: 'hot', label: '🔥 Hot' },
            { id: 'newest', label: 'Newest' },
            { id: 'lowest', label: 'Lowest start' },
          ] as const).map(t => (
            <button
              key={t.id}
              role="tab"
              aria-selected={sortBy === t.id}
              className={`sort-tab ${sortBy === t.id ? 'active' : ''}`}
              onClick={() => setSortBy(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <span className="product-count">
          {liveItems.length} {liveItems.length === 1 ? 'piece' : 'pieces'} live
        </span>
      </div>

      <div className="products-section">
        {liveItems.length === 0 ? (
          <div className="shop-empty">
            <span className="shop-empty-icon">🧥</span>
            <h3>Nothing live right now</h3>
            <p>New pieces drop here as soon as Jalen lists them. Tap the heart on any item later to get notified.</p>
            <div className="shop-empty-steps">
              <div className="step"><span>1</span><p>See a piece you want</p></div>
              <div className="step"><span>2</span><p>Submit your offer</p></div>
              <div className="step"><span>3</span><p>If accepted, pay via PayPal — ships within 48h</p></div>
            </div>
          </div>
        ) : (
          <div className="products-grid">
            {liveItems.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopPage;
